import React, { useState } from 'react'
import { adminQueryApi } from '../../config/api'

const AdminQueryPage = () => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setResults(null)

    if (!query.trim()) {
      setError('SQL 쿼리를 입력해주세요.')
      return
    }

    setIsLoading(true)

    try {
      const response = await adminQueryApi.executeQuery(query.trim())
      
      if (response.success) {
        setResults(response.data)
        setSuccess(`쿼리 실행 성공! ${response.count}개의 결과를 찾았습니다.`)
      } else {
        setError(response.error || '쿼리 실행에 실패했습니다.')
      }
    } catch (err) {
      console.error('Query execution error:', err)
      setError(err.message || '쿼리 실행 중 오류가 발생했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClear = () => {
    setQuery('')
    setResults(null)
    setError('')
    setSuccess('')
  }

  const handlePresetQuery = (presetQuery) => {
    setQuery(presetQuery)
    setError('')
    setSuccess('')
    setResults(null)
  }

  const presetQueries = [
    {
      name: '모든 포스트 조회',
      query: 'SELECT * FROM post LIMIT 10;'
    },
    {
      name: 'YouTube 요약 포스트만 조회',
      query: "SELECT post_id, title, post_type, video_id, channel_title FROM post WHERE post_type = 'youtube_summary' ORDER BY post_id DESC LIMIT 10;"
    },
    {
      name: '최근 포스트 5개',
      query: 'SELECT post_id, title, user_id, post_type, like_count, comment_count FROM post ORDER BY post_id DESC LIMIT 5;'
    },
    {
      name: '포스트 타입별 개수',
      query: 'SELECT post_type, COUNT(*) as count FROM post GROUP BY post_type;'
    },
    {
      name: '사용자별 포스트 수',
      query: 'SELECT user_id, COUNT(*) as post_count FROM post GROUP BY user_id ORDER BY post_count DESC LIMIT 10;'
    },
    {
      name: '테이블 목록',
      query: "SELECT name FROM sqlite_master WHERE type='table';"
    }
  ]

  return (
    <div className="admin-query-page">
      <div className="page-header">
        <h2>🔍 DB 쿼리 도구</h2>
        <p>데이터베이스에 직접 SQL 쿼리를 실행할 수 있습니다.</p>
      </div>

      <div className="query-form-section card">
        <form onSubmit={handleSubmit} className="query-form">
          <div className="form-group">
            <label htmlFor="sql-query">SQL 쿼리</label>
            <textarea
              id="sql-query"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="SELECT * FROM post LIMIT 10;"
              className="form-textarea"
              rows={6}
              disabled={isLoading}
            />
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isLoading || !query.trim()}
            >
              {isLoading ? (
                <>
                  <span className="loading-spinner">🔄</span>
                  실행 중...
                </>
              ) : (
                '▶️ 쿼리 실행'
              )}
            </button>
            <button 
              type="button" 
              onClick={handleClear}
              className="clear-btn"
              disabled={isLoading}
            >
              🗑️ 초기화
            </button>
          </div>
        </form>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="success-message">
            <span className="success-icon">✅</span>
            <span>{success}</span>
          </div>
        )}
      </div>

      <div className="preset-queries-section card">
        <h3>📋 프리셋 쿼리</h3>
        <div className="preset-grid">
          {presetQueries.map((preset, index) => (
            <button
              key={index}
              onClick={() => handlePresetQuery(preset.query)}
              className="preset-btn"
              disabled={isLoading}
            >
              <div className="preset-name">{preset.name}</div>
              <div className="preset-query">{preset.query}</div>
            </button>
          ))}
        </div>
      </div>

      {results && (
        <div className="results-section card">
          <h3>📊 쿼리 결과</h3>
          <div className="results-info">
            <span className="result-count">
              총 {results.length}개의 결과
            </span>
            <button 
              onClick={() => navigator.clipboard.writeText(JSON.stringify(results, null, 2))}
              className="copy-btn"
            >
              📋 결과 복사
            </button>
          </div>
          
          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  {results.length > 0 && Object.keys(results[0]).map((key, index) => (
                    <th key={index}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {Object.values(row).map((value, colIndex) => (
                      <td key={colIndex}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="info-section card">
        <h3>ℹ️ 사용 안내</h3>
        <ul>
          <li><strong>SELECT</strong> 쿼리만 실행 가능합니다 (읽기 전용)</li>
          <li>INSERT, UPDATE, DELETE 등의 수정 쿼리는 실행되지 않습니다</li>
          <li>복잡한 쿼리는 타임아웃될 수 있습니다</li>
          <li>프리셋 쿼리를 사용하면 빠르게 테스트할 수 있습니다</li>
        </ul>
      </div>
    </div>
  )
}

export default AdminQueryPage